import React, { useState, useEffect } from "react";
import "bootstrap-icons/font/bootstrap-icons.css";
import { fetchAllowedMenus } from "../Services/APIService";
import { useNavigate } from "react-router-dom";
import { handleLogout } from "../Services/APIService";
import "../css/sidebar.css";

interface SubMenuItem {
  subMenuId: number;
  subMenuName: string;
  subMenuUrl: string;
}

interface MenuItem {
  menuID: number;
  menuName: string;
  menuUrl: string;
  icon: string;
  displayPosition?: number;   // ← add ? to make it optional
  subMenuList?: SubMenuItem[];
}

const CollapsibleSidebar = () => {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [openSubmenuIndex, setOpenSubmenuIndex] = useState<number | null>(null);
  const [allowedMenus, setAllowedMenus] = useState<MenuItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeMenuId, setActiveMenuId] = useState<number | null>(null);
  const [isMobileToggled, setIsMobileToggled] = useState<boolean>(false);

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  // Add type annotations for index, hasSubmenu, and e
  const toggleSubmenu = (
    index: number,
    hasSubmenu: boolean,
    e: React.MouseEvent<HTMLLIElement, MouseEvent>
  ) => {
    // Don't toggle if clicking on a submenu item
    if (e.target instanceof Element && e.target.closest(".azira-submenu"))
      return;

    if (hasSubmenu) {
      setOpenSubmenuIndex(openSubmenuIndex === index ? null : index);
      e.preventDefault();
    }
  };

  useEffect(() => {
    const getMenus = async () => {
      setIsLoading(true);
      try {
        const menus = await fetchAllowedMenus();
        console.log("Fetched menus:", menus);

        // Sort by displayPosition
        const sorted = menus.sort((a, b) => (a.displayPosition ?? 999) - (b.displayPosition ?? 999));
        setAllowedMenus(sorted);  // ← use sorted instead of menus

        const activeMenuID = localStorage.getItem("activeMenuID");
        if (activeMenuID) {
          const parsedId = parseInt(activeMenuID);
          setActiveMenuId(parsedId);
        }
      } catch (error) {
        console.error("Error fetching menus:", error);
      } finally {
        setIsLoading(false);
      }
    };
    getMenus();

    const intervalId = setInterval(() => {
      fetchAllowedMenus();
    }, 5000);

    return () => clearInterval(intervalId);
  }, []);

  const onLogout = (e: React.MouseEvent) => {
    e.preventDefault();
    handleLogout(navigate);
  };

  useEffect(() => {
    const checkMobileToggle = () => {
      const isToggle = localStorage.getItem("isMobileToggled");
      setIsMobileToggled(isToggle === "true");
    };

    // Check initially
    checkMobileToggle();

    // Listen for storage changes
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "isMobileToggled") {
        // Fixed: should be "isMobileToggled" not "isToggle"
        setIsMobileToggled(e.newValue === "true");
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Also check periodically for same-tab changes
    const interval = setInterval(checkMobileToggle, 100);

    localStorage.removeItem("isMobileToggled");

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // Static icons for different menu types
  const getMenuIcon = (menuName: string) => {
    const iconMap: { [key: string]: string } = {
      "User Management": "bi bi-people",
      Master: "bi bi-database",
      Project: "bi bi-folder",
      "Lead Management": "bi bi-person-plus",
      Campaign: "bi bi-megaphone",
      "Access Permission": "bi bi-shield-lock",
      Dashboard: "bi bi-grid",
      Settings: "bi bi-gear",
      Reports: "bi bi-bar-chart",
      Analytics: "bi bi-graph-up",
      "Subscription Plans": "bi bi-card-checklist",
      Tenant: "bi bi-people",
      Report: "bi bi-file-earmark-text",
    };

    // Return specific icon if found, otherwise return a default icon
    return iconMap[menuName] || "bi bi-circle";
  };

  const handleMenuClick = (menuUrl: string, e: React.MouseEvent) => {
    if (menuUrl) {
      // Navigate to the menu URL
      window.location.href = menuUrl;
    }
  };

  const handleSubmenuClick = (
    subMenuUrl: string,
    menuID: number,
    subMenuId: number,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    if (subMenuUrl) {
      // Navigate to the submenu URL
      //window.location.href = subMenuUrl;
      navigate(subMenuUrl, { state: { menuID, subMenuId } });
    }
  };

  return (
    <div className="azira-app-container">
      <div
        className={`azira-sidebar ${isSidebarCollapsed ? "azira-sidebar-collapsed" : ""
          }${isMobileToggled ? "azira-sidebar-mobile-visible" : ""}`}
      >
        <div className="azira-sidebar-header">
          <div className="azira-logo">
            {/* <div className="azira-logo-icon">
             Z 
            </div>
            <span className="azira-logo-text">Zentro</span> */}
            <img
              src="/img/Zentro-Icon.png"
              className="azira-logo-icon"
              alt=""
            />
            <img
              src="/img/Zentro_Name_Logo.png"
              className="azira-logo-text"
              alt=""
            />
          </div>
          <button
            className="azira-collapse-btn d-none d-sm-block"
            onClick={toggleSidebar}
          >
            <i className="bi bi-chevron-left"></i>
          </button>
        </div>
        <ul className="azira-nav-menu">
          {allowedMenus.map((item, index) => (
            <li
              key={item.menuID}
              className={`azira-menu-item ${openSubmenuIndex === index ? "azira-menu-open" : ""
                } ${activeMenuId === item.menuID ? "azira-menu-active" : ""}`}
              onClick={(e) =>
                toggleSubmenu(
                  index,
                  !!(item.subMenuList && item.subMenuList.length > 0),
                  e
                )
              }
            >
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (!item.subMenuList || item.subMenuList.length === 0) {
                    handleMenuClick(item.menuUrl, e);
                  }
                }}
              >
                <span className="azira-menu-icon">
                  <i className={getMenuIcon(item.menuName)}></i>
                </span>
                <span className="azira-menu-text">{item.menuName}</span>
                {item.subMenuList && item.subMenuList.length > 0 && (
                  <span className="azira-dropdown-icon">
                    <i className="bi bi-chevron-down"></i>
                  </span>
                )}
              </a>
              {item.subMenuList && item.subMenuList.length > 0 && (
                <ul className="azira-submenu">
                  {item.subMenuList.map((subItem) => (
                    <li key={subItem.subMenuId} className="azira-submenu-item">
                      <a
                        href={subItem.subMenuUrl || "#"}
                        onClick={(e) => {
                          e.preventDefault();
                          handleSubmenuClick(
                            subItem.subMenuUrl,
                            item.menuID,
                            subItem.subMenuId,
                            e
                          );
                        }}
                      >
                        {subItem.subMenuName}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
        <a onClick={onLogout}>
          {" "}
          <div className="azira-weather-widget d-block d-sm-none d-flex justify-content-center align-items-center">
            <div className="azira-weather-icon">
              <i className="bi bi-box-arrow-right"></i>
            </div>
            <div className="azira-weather-text">
              <div className="azira-temp">Logout</div>
            </div>
          </div>
        </a>
      </div>

      <div className="azira-content">{/* Your main content goes here */}</div>
    </div>
  );
};

export default CollapsibleSidebar;
